// Which open tabs hold unsaved sidebar edits, for the readers that render
// OUTSIDE the WorkspaceProvider.
//
// Logging out tears the whole workspace down: every tab's sidebar unmounts and
// takes its unsaved section edits with it, and it is an in-app navigation, so
// no beforeunload prompt fires either. Switching user group does it by a page
// reload. Both are offered from the app bar, and the app bar renders in
// MainContainer, above Main, where the provider mounts, so `useWorkspace()`
// is null there.
//
// So the provider publishes the one fact the app bar needs, and the app bar
// reads it at the moment of the click. A module value rather than lifting the
// provider: the provider is deliberately scoped to the signed-in app shell
// (TGCLOUD-1618), and nothing here needs to re-render when it changes.
//
// Each entry is { sessionId, title, unsavedSections } for a tab whose
// `unsavedSections` is non-empty (TGCLOUD-1839).

import { hasUnsavedChanges } from './workspaceReducer';

let published = [];

export const unsavedWorkOf = (state) =>
  (state?.sessions || [])
    .filter(hasUnsavedChanges)
    .map(({ sessionId, title, unsavedSections, unsavedCount }) => ({
      sessionId,
      title,
      unsavedSections: [...unsavedSections],
      // How many fields hold a change, where the sidebar reported it
      // (TGCLOUD-1902).
      ...(Number.isFinite(unsavedCount) ? { unsavedCount } : null)
    }));

/**
 * How many unsaved changes these tabs hold: each tab's count where its sidebar
 * reported one, otherwise one per unsaved section. Undefined for none.
 */
export const unsavedChangeCountOf = (sessions = []) => {
  const total = sessions.reduce(
    (sum, s) =>
      sum +
      (Number.isFinite(s?.unsavedCount) && s.unsavedCount > 0
        ? s.unsavedCount
        : (s?.unsavedSections || []).length),
    0
  );
  return total > 0 ? total : undefined;
};

export const publishUnsavedWork = (work) => {
  published = Array.isArray(work) ? work : [];
};

export const getUnsavedWork = () => published;

// A page unload the clinician has already agreed to in the app's own prompt
// ("Discard and switch group"). The save footer's beforeunload prompt stands
// down for it, so they are not asked the same question twice, and a Cancel on
// the second one cannot strand them on a page whose group the server has
// already switched.
let unloadAgreed = false;

export const agreeToUnloadWithUnsavedWork = () => {
  unloadAgreed = true;
};

export const unloadWithUnsavedWorkAgreed = () => unloadAgreed;

// For tests: both values are module state and outlive a render.
export const resetUnsavedWork = () => {
  published = [];
  unloadAgreed = false;
};
